import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/AuthContext";
import { CartProvider } from "./lib/CartContext";
import { MembershipProvider } from "./lib/MembershipContext";
import UpgradeModal from "./components/UpgradeModal";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import { KingdomLoader } from "./components/kit";
import Home from "./pages/Home";

// Lazy-loaded routes — keeps the landing page fast and defers heavy libs
// (html2canvas/jspdf live inside game/parent components) into their own chunks.
const About = lazy(() => import("./pages/About"));
const Books = lazy(() => import("./pages/Books"));
const Friends = lazy(() => import("./pages/Friends"));
const Feelings = lazy(() => import("./pages/Feelings"));
const Activities = lazy(() => import("./pages/Activities"));
const Coloring = lazy(() => import("./pages/Coloring"));
const Events = lazy(() => import("./pages/Events"));
const Resources = lazy(() => import("./pages/Resources"));
const Blog = lazy(() => import("./pages/Blog"));
const SegmentSignup = lazy(() => import("./pages/SegmentSignup"));
const Membership = lazy(() => import("./pages/Membership"));
const Redeem = lazy(() => import("./pages/Redeem"));
const Contact = lazy(() => import("./pages/Contact"));
const HeartQuest = lazy(() => import("./pages/HeartQuest"));
const WordSearch = lazy(() => import("./pages/WordSearch"));
const PuzzleCenter = lazy(() => import("./pages/PuzzleCenter"));
const PuzzlePlay = lazy(() => import("./pages/PuzzlePlay"));
const AdaptiveActivity = lazy(() => import("./pages/AdaptiveActivity"));
const RoyalChallenge = lazy(() => import("./pages/RoyalChallenge"));
const KindnessGarden = lazy(() => import("./pages/KindnessGarden"));
const FeelingsMatch = lazy(() => import("./pages/FeelingsMatch"));
const MoodMonster = lazy(() => import("./pages/kingdom/MoodMonster"));
const WordOfWeek = lazy(() => import("./pages/WordOfWeek"));
const KingdomDictionary = lazy(() => import("./pages/KingdomDictionary"));
const TeacherHub = lazy(() => import("./pages/TeacherHub"));
const ClassroomPack = lazy(() => import("./pages/ClassroomPack"));
const LegalPage = lazy(() => import("./pages/legal/LegalPage"));
const KingdomLogin = lazy(() => import("./pages/kingdom/Login"));
const ChildLogin = lazy(() => import("./pages/kingdom/ChildLogin"));
const ParentDashboard = lazy(() => import("./pages/kingdom/ParentDashboard"));
const ChildDashboard = lazy(() => import("./pages/kingdom/ChildDashboard"));
const YearInKingdom = lazy(() => import("./pages/kingdom/YearInKingdom"));
const FeelingsCalendarPage = lazy(() => import("./pages/kingdom/FeelingsCalendarPage"));
const FeelingsShareView = lazy(() => import("./pages/FeelingsShareView"));
const KingdomAdventure = lazy(() => import("./pages/adventure/KingdomAdventure"));
const PortraitStudioPreview = lazy(() => import("./pages/adventure/PortraitStudioPreview"));
const StorybookChildPreview = lazy(() => import("./pages/adventure/StorybookChildPreview"));
const StorybookReviewWalk = lazy(() => import("./pages/adventure/StorybookReviewWalk"));
const CreatorPreview = lazy(() => import("./pages/adventure/CreatorPreview"));
const Admin = lazy(() => import("./pages/kingdom/Admin"));
const KingdomJournal = lazy(() => import("./pages/kingdom/KingdomJournal"));
const LessonPlanBuilder = lazy(() => import("./pages/kingdom/LessonPlanBuilder"));
const TeacherDashboard = lazy(() => import("./pages/kingdom/TeacherDashboard"));
const SharedLessonPlan = lazy(() => import("./pages/kingdom/SharedLessonPlan"));
const Showcase = lazy(() => import("./pages/Showcase"));
const PaymentSuccess = lazy(() => import("./pages/PaymentResult").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("./pages/PaymentResult").then((m) => ({ default: m.PaymentCancel })));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const NewsletterConfirm = lazy(() => import("./pages/NewsletterConfirm"));
const NewsletterManage = lazy(() => import("./pages/NewsletterManage"));
const Watch = lazy(() => import("./pages/Watch"));
const SkyLanding = lazy(() => import("./pages/sky/SkyLanding"));
const SkyActivity = lazy(() => import("./pages/sky/SkyActivity"));
const SkyRedirect = lazy(() => import("./pages/sky/SkyRedirect"));
const Passport = lazy(() => import("./pages/sky/Passport"));
const StampCollection = lazy(() => import("./pages/sky/StampCollection"));

const LEGAL = ["consent", "privacy", "terms", "cookies", "data-deletion", "copyright"];

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
      <MembershipProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors toastOptions={{ style: { fontFamily: "Nunito" } }} />
        <UpgradeModal />
        <Suspense fallback={<KingdomLoader />}>
          <Routes>
            {/* Public site */}
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/books" element={<Books />} />
              <Route path="/friends" element={<Friends />} />
              <Route path="/feelings" element={<Feelings />} />
              <Route path="/activities" element={<Activities />} />
              <Route path="/coloring" element={<Coloring />} />
              <Route path="/watch" element={<Watch />} />
              <Route path="/watch/series/:seriesSlug" element={<Watch />} />
              <Route path="/watch/:slug" element={<Watch />} />
              <Route path="/adventures/:slug" element={<SkyLanding />} />
              <Route path="/adventures/:slug/play/:page" element={<SkyActivity />} />
              <Route path="/adventures/ginger-heart-airlines/passport" element={<Passport />} />
              <Route path="/adventures/ginger-heart-airlines/stamps" element={<StampCollection />} />
              <Route path="/sky" element={<SkyRedirect />} />
              <Route path="/events" element={<Events />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/join" element={<SegmentSignup />} />
              <Route path="/join/:segment" element={<SegmentSignup />} />
              <Route path="/membership" element={<Membership />} />
              <Route path="/redeem" element={<Redeem />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/heart-quest" element={<HeartQuest />} />
              <Route path="/word-search" element={<WordSearch />} />
              <Route path="/puzzles" element={<PuzzleCenter />} />
              <Route path="/puzzles/play/:slug" element={<PuzzlePlay />} />
              <Route path="/puzzles/activity/:family" element={<AdaptiveActivity />} />
              <Route path="/royal-challenge" element={<RoyalChallenge />} />
              <Route path="/kindness-garden" element={<KindnessGarden />} />
              <Route path="/feelings-match" element={<FeelingsMatch />} />
              <Route path="/word-of-week" element={<WordOfWeek />} />
              <Route path="/kingdom-dictionary" element={<KingdomDictionary />} />
              <Route path="/teacher-hub" element={<TeacherHub />} />
              <Route path="/pack/:share" element={<ClassroomPack />} />
              {LEGAL.map((s) => <Route key={s} path={`/${s}`} element={<LegalPage slug={s} />} />)}
            </Route>

            {/* Standalone printer-friendly shared plan (no public navbar) */}
            <Route path="/mood-monster" element={<MoodMonster />} />
            <Route path="/plan/:share" element={<SharedLessonPlan />} />
            <Route path="/showcase" element={<Showcase />} />
            <Route path="/feelings-share/:token" element={<FeelingsShareView />} />
            <Route path="/kingdom-adventure" element={<KingdomAdventure />} />
            <Route path="/portrait-studio-preview" element={<PortraitStudioPreview />} />
            <Route path="/storybook-child-preview" element={<StorybookChildPreview />} />
            <Route path="/storybook-review" element={<StorybookReviewWalk />} />
            <Route path="/creator-preview" element={<CreatorPreview />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/newsletter/confirm" element={<NewsletterConfirm />} />
            <Route path="/newsletter/manage" element={<NewsletterManage mode="manage" />} />
            <Route path="/newsletter/unsubscribe" element={<NewsletterManage mode="unsubscribe" />} />

            {/* Private testing — Ginger Hearts family accounts */}
            <Route path="/kingdom/login" element={<KingdomLogin />} />
            <Route path="/signup" element={<KingdomLogin />} />
            <Route path="/kingdom/login/child" element={<Navigate to="/kingdom/login" replace />} />
            <Route path="/kingdom/login/child-legacy" element={<ChildLogin />} />
            <Route path="/kingdom" element={<ProtectedRoute role="parent"><ParentDashboard /></ProtectedRoute>} />
            <Route path="/kingdom/play/:childId" element={<ProtectedRoute role="parent" allowChild><ChildDashboard /></ProtectedRoute>} />
            <Route path="/kingdom/feelings-calendar/:childId" element={<ProtectedRoute role="parent"><FeelingsCalendarPage /></ProtectedRoute>} />
            <Route path="/kingdom/journal/:childId" element={<ProtectedRoute role="parent"><KingdomJournal /></ProtectedRoute>} />
            <Route path="/kingdom/keepsake" element={<ProtectedRoute role="parent"><YearInKingdom /></ProtectedRoute>} />
            <Route path="/kingdom/lesson-plans" element={<ProtectedRoute role="parent"><LessonPlanBuilder /></ProtectedRoute>} />
            <Route path="/teacher" element={<ProtectedRoute role="educator"><TeacherDashboard /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute role="admin"><Admin /></ProtectedRoute>} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </MembershipProvider>
      </CartProvider>
    </AuthProvider>
  );
}
